import { useEffect } from "react";
import { trackEventOnce } from "../../services/analytics/track";
import PrettoSlider from "./PrettoSlider";
import Numeral from "../numeral/Numeral";
import { RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import moment from "moment";

import { useCreditDraftStore } from "../../store/creditDraftStore";

import { getInterest } from "../../utils/creditUser/getInterest";

import { calculateInterest } from "../../services/credits/InterestService";
import { calculateFirma } from "../../services/credits/FirmaService";

import { useTmlv } from "../../hooks/useTmlv";
import { calcularFechas } from "../credits/calculoFechas";

const fechasOptions = calcularFechas();
const dateLimit1 = fechasOptions.fecha1.format("DD/MM/YYYY");
const dateLimit2 = fechasOptions.fecha2.format("DD/MM/YYYY");
const vence1 = fechasOptions.fecha1.diff(moment(), "days");
const vence2 = fechasOptions.fecha2.diff(moment(), "days");

const datepay = [
  {
    id: 1,
    day: fechasOptions.fecha1Day,
    datelimit: dateLimit1,
    value: vence1 + 1,
    expira: `Vence en ${vence1 + 1} días`,
  },
  {
    id: 2,
    day: fechasOptions.fecha2Day,
    datelimit: dateLimit2,
    value: vence2 + 1,
    expira: `Vence en ${vence2 + 1} días`,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function SimulatorV2(props) {
  const { draft, saveDraft, loadDraft } = useCreditDraftStore();
  const { mesUsura, tasaUsura, tasaUsuraTmlvRate } = useTmlv();

  // Cargar draft al montar
  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  // Valores desde store o defaults
  const Amount = draft?.requestedAmount ?? props.default;
  const selectedMailingLists = draft?.selectedMailingLists ?? datepay[1];
  const checkFirmaElectronica = draft?.checkFirmaElectronica ?? true;
  const interest = draft?.interest ?? 0;
  const total = draft?.totalToPay ?? 0;
  const totalFirmaElectronica = draft?.totalFirmaElectronica ?? 0;
  const dctoFirmaElectronica = draft?.dctoFirmaElectronica ?? 0;

  //Handlers
  const recalculate = (amount, vencimientoEnDias, date, override = {}) => {
    // Leer el draft actual desde el store (evita stale closures)
    const currentDraft = useCreditDraftStore.getState().draft || {};

    // Si el override especifica checkFirmaElectronica, úsalo; si no, toma el del draft o el default true
    const check =
      override.checkFirmaElectronica !== undefined
        ? override.checkFirmaElectronica
        : currentDraft.checkFirmaElectronica ?? true;

    // interés usando servicio puro
    const newInterest = calculateInterest(amount, vencimientoEnDias, {
      tea: localStorage.getItem("currentInterestTea"),
      tasaUsuraTmlv: currentDraft?.tasaUsuraTmlvRate,
    });

    // firma usando servicio puro (usamos "check" que acabamos de determinar)
    const { totalFirmaElectronica, dctoFirmaElectronica } = calculateFirma(
      amount,
      vencimientoEnDias,
      check
    );

    // total
    const newTotal = amount + newInterest + (check ? totalFirmaElectronica : 0);

    // Construimos updatedDraft a partir del draft actual para preservar todo
    const updatedDraft = {
      ...currentDraft,
      requestedAmount: amount,
      termInDays: vencimientoEnDias,
      totalToPay: newTotal,
      paydayLimit:
        date?.datelimit ?? currentDraft.selectedMailingLists?.datelimit,
      interest: newInterest,
      totalFirmaElectronica,
      dctoFirmaElectronica,
      selectedMailingLists: date ?? currentDraft.selectedMailingLists,
      checkFirmaElectronica: check,
      updatedAt: new Date().toISOString(),
    };

    saveDraft(updatedDraft);
  };

  const handleChangeOption = (date) => {
    const vencimientoEnDias =
      moment(date.datelimit, "DD/MM/YYYY").diff(moment(), "days") + 1;
    trackEventOnce("simulador-inicio", "simulador_usado", { simulador: "inicio", monto: Amount, plazo_dias: vencimientoEnDias });
    recalculate(Amount, vencimientoEnDias, date);
  };

  const onChangeSlider = (amount) => {
    const vencimientoEnDias =
      moment(selectedMailingLists.datelimit, "DD/MM/YYYY").diff(
        moment(),
        "days"
      ) + 1;
    trackEventOnce("simulador-inicio", "simulador_usado", { simulador: "inicio", monto: amount, plazo_dias: vencimientoEnDias });
    recalculate(amount, vencimientoEnDias, selectedMailingLists);
  };

  const handleCheckFirmaElectronica = (checked) => {
    saveDraft({ checkFirmaElectronica: checked });

    // Calcular vencimiento
    const vencimientoEnDias =
      moment(selectedMailingLists.datelimit, "DD/MM/YYYY").diff(
        moment(),
        "days"
      ) + 1;

    // Llamar recalculate pasando explicitamente el nuevo valor para evitar usar valores stale
    recalculate(Amount, vencimientoEnDias, selectedMailingLists, {
      checkFirmaElectronica: checked,
    });
  };

  useEffect(() => {
    (async () => {
      await getInterest();
      const vencimientoEnDias = selectedMailingLists.value;
      recalculate(Amount, vencimientoEnDias, selectedMailingLists);
    })();
    // selectedMailingLists ya dispara este recálculo vía handleChangeOption;
    // agregarlo aquí duplicaría la llamada a getInterest()/recalculate().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Amount]);

  return (
    <div>
      {/* Slider monto */}
      <section aria-labelledby="simulator-title">
        <div className="relative pt-1">
          <div className="flex justify-between">
            <h2 id="simulator-title" className="text-lg font-bold">
              {props.title}
            </h2>
            <Numeral
              className="text-lg font-bold"
              format={"$0,0"}
              value={Amount}
            />
          </div>
          <PrettoSlider
            value={Amount}
            step={props.step}
            onChange={(event, vAmnt) => onChangeSlider(vAmnt)}
            defaultValue={Amount}
            max={props.maxAmn}
            min={props.minAmn}
          />
        </div>
      </section>

      {/* Fechas de pago */}
      <section aria-labelledby="simulator-limitDate">
        <div className="pt-4">
          <h2 id="simulator-limitDate" className="text-lg font-bold">
            {props.title3}
          </h2>
          <RadioGroup
            value={selectedMailingLists}
            onChange={handleChangeOption}
          >
            <div className="mt-0 grid gap-y-6 grid-cols-2 gap-x-4">
              {datepay.map((datePay, index) => (
                <RadioGroup.Option
                  key={datePay.id}
                  value={datePay}
                  className={({ checked, focus }) =>
                    classNames(
                      checked
                        ? "border-transparent"
                        : "border-purpleLight border-2",
                      focus ? "border-purple border-2" : "",
                      "relative border-2 rounded-lg shadow-xs px-2 py-1 flex cursor-pointer"
                    )
                  }
                >
                  {({ checked, focus }) => (
                    <>
                      <span className="flex-1 flex flex-col cursor-pointer text-xl">
                        {datePay.datelimit}
                      </span>
                      <CheckCircleIcon
                        className={classNames(
                          !checked ? "invisible" : "",
                          "h-5 w-5 text-green"
                        )}
                        aria-hidden="true"
                      />
                      <span
                        key={`${index}-span-root2`}
                        className={classNames(
                          focus ? "border-purple border-2" : "border-2",
                          checked ? "border-purple" : "border-transparent",
                          "absolute -inset-px rounded-lg pointer-events-none"
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>

          <div className="mt-1 grid gap-y-6 grid-cols-2 gap-x-4">
            {datepay.map((datePayY, idx) => (
              <span key={idx} className="text-sm pl-2">
                {datePayY.expira}
              </span> /* Vence en x días */
            ))}
          </div>
        </div>
      </section>

      {/* Costos */}
      <section aria-labelledby="simulator-costos">
        <div className="mt-4">
          <h2 className="font-bold">Costos fijos</h2>
          <div className="flex justify-between text-sm">
            <span>Monto solicitado</span>
            <div className="mr-4">
              <Numeral format={"$0,0"} value={Amount} />
            </div>
          </div>

          <div className="flex justify-between text-sm">
            <span>
              Interés corriente{" "}
              <span className="text-sm">({tasaUsuraTmlvRate ?? 25}% E.A)</span>
            </span>
            <div className="flex">
              <Numeral format={"$0,0"} value={interest} />
              <InformationCircleIcon
                data-tooltip-id="int"
                className="w-4 h-auto ml-0.5 cursor-pointer"
              />
              <Tooltip
                id="int"
                place="top"
                className="w-72"
              >
                (Efectivo Anual) sobre el capital adeudado, para {mesUsura}, la
                tasa máxima en Colombia es {tasaUsura}
              </Tooltip>
            </div>
          </div>

          <h2 className="font-bold">Costo opcional</h2>
          <div className="md:text-sm flex justify-between gap-1">
            <div className="flex gap-1">
              <label htmlFor="sign-e" className="sr-only">
                Aceptar firma electrónica
              </label>
              <input
                id="sign-e"
                name="sign-e"
                type="checkbox"
                className="h-4 w-4 text-purple focus:ring-0 border-tdark rounded-sm cursor-pointer"
                onChange={(e) => handleCheckFirmaElectronica(e.target.checked)}
                checked={draft?.checkFirmaElectronica ?? true}
              />
              <span>
                Firma electrónica{" "}
                <span className="text-sm">(con descuento)</span>
              </span>
            </div>
            <div className="flex">
              <Numeral format={"$0,0"} value={totalFirmaElectronica} />
              <InformationCircleIcon
                data-tooltip-id="admon"
                className="w-4 h-auto ml-0.5 cursor-pointer"
              />
              <div className="font-bold">
                <Tooltip
                  place="top"
                  id="admon"
                  className="w-72"
                >
                  La Firma Electrónica es el costo opcional que de manera
                  libre y voluntaria es aceptado por ti, posterior a la
                  solicitud y aprobación del crédito. El costo del servicio de
                  Firma Electrónica es un costo fijo de $145.000 pesos al que
                  podemos aplicar un descuento de (
                  {checkFirmaElectronica === true ? (
                    <Numeral
                      format={"$0,0"}
                      value={
                        dctoFirmaElectronica === 0
                          ? "0"
                          : dctoFirmaElectronica
                      }
                    />
                  ) : (
                    "0"
                  )}
                  ) por pronto pago, en caso de que entres en mora este
                  descuento puede cambiar o anularse. Al ser un costo opcional
                  puedes aceptar adquirlo o no. En el caso de que tu solicitud
                  sea aprobada verás una vista que te permitirá elegir usar
                  nuestra Firma Electrónica o conocer el proceso para firmar
                  en físico.
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="flex justify-between font-bold">
            <span>Total a pagar</span>
            <Numeral format={"$0,0"} value={total} />
          </div>

          <div className="flex justify-between text-sm">
            <span>Fecha de tu primera cuota</span>
            <span className="text-sm">
              {draft?.paydayLimit ?? selectedMailingLists?.datelimit}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
